import { MouseEvent } from "react";
import { getPlayerRankLinks } from "../../utils/leaderboard/playerLinks";
import { playerRosterName } from "../../utils/leaderboard/player-display";

interface PlayerExternalLinksProps {
  displayName: string;
  tagLine: string;
  /** Visible roster name used for aria labels when it differs from displayName. */
  gameName?: string;
  rankSiteLabel: (site: string) => string;
  size?: "sm" | "md";
  stopPropagation?: boolean;
  className?: string;
}

/**
 * `player-external-links` / `player-external-link` are kept as class hooks so host
 * pages (e.g. the player profile sidebar) can restyle the whole set at once.
 */
export const PlayerExternalLinks = ({
  displayName,
  tagLine,
  gameName = "",
  rankSiteLabel,
  size = "md",
  stopPropagation = false,
  className = "",
}: PlayerExternalLinksProps) => {
  const rankLinks = getPlayerRankLinks(displayName, tagLine);
  const labelName = playerRosterName(gameName) || displayName.trim();

  const handleClick = (event: MouseEvent) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
  };

  if (!rankLinks.length) {
    return null;
  }

  const iconSize = size === "sm" ? 14 : 16;

  return (
    <div
      className={`player-external-links inline-flex items-center ${
        size === "sm" ? "gap-[0.3rem]" : "gap-[0.35rem]"
      } ${className}`}
      onClick={handleClick}
    >
      {rankLinks.map((link) => (
        <a
          key={link.id}
          href={link.url}
          className={`player-external-link inline-flex items-center justify-center rounded-md border border-[var(--surface-card-border)] bg-[rgba(15,23,42,0.35)] transition-[background,border-color,transform] duration-150 ease-in-out hover:-translate-y-px hover:border-[rgba(123,147,255,0.35)] hover:bg-[rgba(123,147,255,0.12)] hover:no-underline ${
            size === "sm" ? "h-6 w-6" : "h-[1.65rem] w-[1.65rem]"
          }`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={rankSiteLabel(link.label).replace("{player}", labelName)}
          title={rankSiteLabel(link.label).replace("{player}", labelName)}
        >
          <img
            src={link.iconUrl}
            alt=""
            className="block rounded-[2px]"
            width={iconSize}
            height={iconSize}
          />
        </a>
      ))}
    </div>
  );
};
